import { Part } from "../playerConfiguration/Part";
import { ScenarioElement } from "../playerConfiguration/ScenarioElement";
import { Subtitle } from "../playerConfiguration/Subtitle";
import { ScenarioPath } from "../scenarioPath/ScenarioPath";
import { StoredPreferences } from "../storedPreferences/StoredPreferences";
import { PartDebugInfo } from "../playerConfiguration/DebugPartInfo";

export class ScenarioGlobalState {
  private static FULL_VOLUME = 1;

  private _code: string;
  private _campaign: string;
  private _scenarioPath: ScenarioPath;
  private _volume: number;
  private _isMuted: boolean;
  private _selectedSubtitles: Subtitle;
  private _aspectRatio: number;
  private _partsDebugInfo: PartDebugInfo[];

  private _preferredSubtitleLabel: string;
  private _isSubtitlesShownWhenAvailable: boolean;
  private _isDebugModeOpen: boolean;

  private constructor() {}

  get code(): string {
    return this._code;
  }

  get campaign(): string {
    return this._campaign;
  }

  get scenarioPath(): ScenarioPath {
    return this._scenarioPath;
  }

  get volume(): number {
    return this._volume;
  }

  get isMuted(): boolean {
    return this._isMuted;
  }

  get selectedSubtitles(): Subtitle {
    return this._selectedSubtitles;
  }

  get aspectRatio(): number {
    return this._aspectRatio;
  }

  get partsDebugInfo(): PartDebugInfo[] {
    return this._partsDebugInfo;
  }

  static initialState(
    code: string,
    campaign: string,
    scenarioElements: ScenarioElement[],
    scenarioParts: Part[],
    debugPartInfos: PartDebugInfo[],
    aspectRatio: number,
    isMuted: boolean,
    useDefaultSubtitles: boolean,
    storedPreferences: StoredPreferences,
  ): ScenarioGlobalState {
    const state = new ScenarioGlobalState();
    state._code = code;
    state._campaign = campaign;
    state._scenarioPath = ScenarioPath.initialize(scenarioElements, scenarioParts);
    state._volume = storedPreferences.volume !== undefined ? storedPreferences.volume : ScenarioGlobalState.FULL_VOLUME;
    state._isMuted = !state._volume || isMuted;
    state._selectedSubtitles = undefined;
    state._aspectRatio = aspectRatio;
    state._preferredSubtitleLabel = storedPreferences.subtitlesLanguage;
    state._partsDebugInfo = debugPartInfos;
    state._isSubtitlesShownWhenAvailable =
      storedPreferences.isSubtitlesDisplayed !== undefined ? storedPreferences.isSubtitlesDisplayed : useDefaultSubtitles;
    state._isDebugModeOpen = storedPreferences.isDebugModeOpen;

    return state;
  }
  withNowPlaying(scenarioElement: ScenarioElement): ScenarioGlobalState {
    const copy = this.copy();
    copy._scenarioPath = copy._scenarioPath.nowPlaying(scenarioElement);

    if (copy._isSubtitlesShownWhenAvailable) {
      copy._selectedSubtitles = copy._preferredSubtitleLabel
        ? copy.findSubtitlesMatchingLabel(scenarioElement, copy._preferredSubtitleLabel)
        : copy.findDefaultSubtitles(scenarioElement);
    }

    return copy;
  }

  withDebugModeOpened(isDebugModeOpen: boolean): ScenarioGlobalState {
    const copy = this.copy();
    this._isDebugModeOpen = isDebugModeOpen;
    return copy;
  }

  withVolume(volume: number): ScenarioGlobalState {
    const copy = this.copy();
    copy._volume = volume;
    copy._isMuted = volume === 0;
    return copy;
  }

  withMuted(isMuted: boolean): ScenarioGlobalState {
    const copy = this.copy();
    copy._isMuted = isMuted;
    return copy;
  }

  withSelectedSubtitles(subtitles: Subtitle): ScenarioGlobalState {
    const copy = this.copy();
    copy._selectedSubtitles = subtitles;
    copy._isSubtitlesShownWhenAvailable = !!subtitles;
    copy._preferredSubtitleLabel = subtitles && subtitles.label;
    return copy;
  }

  private findSubtitlesMatchingLabel(scenarioElement: ScenarioElement, label: string): Subtitle {
    if (scenarioElement.hasSubtitles) {
      return scenarioElement.video.subtitles.find((subtitles) => subtitles.label === label);
    }
  }

  private findDefaultSubtitles(scenarioElement: ScenarioElement): Subtitle {
    return scenarioElement.hasSubtitles && scenarioElement.video.subtitles[0];
  }

  private copy(): ScenarioGlobalState {
    const copy = new ScenarioGlobalState();
    copy._code = this._code;
    copy._campaign = this._campaign;
    copy._scenarioPath = this._scenarioPath;
    copy._volume = this._volume;
    copy._isMuted = this._isMuted;
    copy._selectedSubtitles = this._selectedSubtitles;
    copy._aspectRatio = this._aspectRatio;
    copy._isSubtitlesShownWhenAvailable = this._isSubtitlesShownWhenAvailable;
    copy._preferredSubtitleLabel = this._preferredSubtitleLabel;
    copy._partsDebugInfo = this._partsDebugInfo;
    copy._isDebugModeOpen = this._isDebugModeOpen;

    return copy;
  }
}
